"use client";

import useSWR from "swr";

import type { LngLat } from "./types";

const GEOCODE_BASE = "https://api.mapbox.com/geocoding/v5/mapbox.places";

/**
 * Minimum Mapbox match relevance to trust. Mapbox fuzzy-matches non-place
 * input (e.g. a facility description like "Order received and queued") to a
 * low-relevance address in a random location (~0.4), while exact ZIP / place
 * matches score ~1.0. Rejecting low-relevance results keeps phantom points
 * off the route map.
 */
export const MIN_GEOCODE_RELEVANCE = 0.6;

interface GeocodeFeature {
  relevance?: number;
  center?: [number, number];
}

/**
 * Pick a confident `LngLat` from a Mapbox geocoding response, or null. A
 * feature with no `relevance` field is trusted; one below `minRelevance` is
 * rejected; missing / non-finite centers are rejected.
 */
export function pickGeocodeCenter(
  body: unknown,
  minRelevance: number,
): LngLat | null {
  const feature = (body as { features?: GeocodeFeature[] } | null)
    ?.features?.[0];
  if (!feature) return null;
  if (
    typeof feature.relevance === "number" &&
    feature.relevance < minRelevance
  ) {
    return null;
  }
  const center = feature.center;
  if (
    Array.isArray(center) &&
    Number.isFinite(center[0]) &&
    Number.isFinite(center[1])
  ) {
    return { lng: center[0], lat: center[1] };
  }
  return null;
}

async function geocodeOne(
  query: string,
  token: string,
): Promise<LngLat | null> {
  const url =
    `${GEOCODE_BASE}/${encodeURIComponent(query)}.json` +
    `?access_token=${encodeURIComponent(token)}&limit=1&country=us`;
  const res = await fetch(url);
  if (!res.ok) return null;
  return pickGeocodeCenter(await res.json(), MIN_GEOCODE_RELEVANCE);
}

export interface UseGeocodeResult {
  /** Resolved coordinates keyed by the original query (null = unresolved). */
  byQuery: Map<string, LngLat | null>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Client-side Mapbox forward geocoding for a set of place queries (ZIPs or
 * "City, ST" strings). Dedupes queries, caches forever (coordinates are
 * stable), and runs only when `NEXT_PUBLIC_MAPBOX_TOKEN` is set (otherwise
 * every query resolves to `null` and `isLoading` stays false).
 *
 * Queries are fetched as one batch keyed by the sorted unique list so SWR
 * dedupes repeated lookups across the same IMb session.
 */
export function useGeocode(queries: string[]): UseGeocodeResult {
  const token = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
  const unique = Array.from(
    new Set(queries.map((q) => q.trim()).filter((q) => q.length > 0)),
  ).sort();

  const swrKey =
    token && unique.length > 0
      ? (["mapbox-geocode-batch", unique.join("|")] as const)
      : null;

  const { data, isLoading, error } = useSWR<Map<string, LngLat | null>>(
    swrKey,
    async () => {
      const resolved = await Promise.all(
        unique.map((q) => geocodeOne(q, token ?? "")),
      );
      return new Map(unique.map((q, i) => [q, resolved[i]]));
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: Number.POSITIVE_INFINITY,
    },
  );

  const byQuery = data ?? new Map<string, LngLat | null>();

  return {
    byQuery,
    isLoading: Boolean(swrKey) && isLoading,
    isError: Boolean(error),
  };
}
